import LiveRegion from './live-region';

export default class DragDropKeyboard {
  constructor(params = {}) {
    const { container, region } = params;
    
    if (container instanceof HTMLElement) {
      this.list = container;
    } else if (typeof container === 'string') {
      this.list = document.querySelector(container);
    }
    
    this.liveRegion = new LiveRegion({container: region});
    this.listItems = null;
    this.activeItem = null;
    
    if (this.list && this.liveRegion) {
      this.initEvents();
    }
  }
  
  /*
   * Initializes event handlers.
   */
  initEvents() {
    this.list.addEventListener('click', this.handleClick.bind(this), false);
    this.list.addEventListener('keydown', this.handleKeydown.bind(this), false);
    this.list.addEventListener('focusout', this.handleFocusout.bind(this), false);
  }
  
  /*
   * Focus placement utility.
   */
  focus(el) {
    if (el) {
      setTimeout(() => el.focus(), 1);
    }
  }
  
  /*
   * Returns the DOM index (position) of the specified element.
   */
  getItemIndex(el) {
    if (!el) {
      return false;
    }
    
    const items = this.list.children;
    const length = items.length;
    const index = Array.prototype.slice.call(items).indexOf(el);

    return {
      index: index,
      length: length
    };
  }
  
  /*
   * Announce the action to screen reader users.
   */
  announceAction(action) {
    const el = this.activeItem;
    const skill = el.dataset.skill;
    const position = this.getItemIndex(el);
    let msg = '';
    
    switch(action) {
      case 'ACTIVATE':
        msg = `${skill} skill active, current position ${++position.index} of ${position.length}. 
          Press up and down keys to change position, Space to confirm new position, Escape to cancel.`;
        break;
      case 'MOVE':
        msg = `${skill} skill moved to position ${++position.index} of ${position.length}.
          Press Space to confirm new position, Escape to cancel.`;
        break;
      case 'CONFIRM':
        msg = `${skill} skill successfully repositioned.`;
        break;
      case 'CANCEL':
        msg = `Cancelled repositioning ${skill} skill.`;
        break;
    }
    
    this.liveRegion.announce(msg);
  }
  
  /*
   * Enable the activated item as `draggable`.
   */
  makeActive(el) {
    let itemToDrag = el && el.parentNode.parentNode;
    
    // If there is already an active item, make it inactive first.
    if (this.activeItem) {
      this.activeItem.classList.remove('active');
      this.resetListOrder();
    }
    
    if (itemToDrag) {
      this.activeItem = itemToDrag;
      this.activeItem.index = this.getItemIndex(this.activeItem).index;
      // Cache original list order in case user cancels `drag` operation.
      this.listItems = this.list.querySelectorAll('li');
      
      this.activeItem.classList.add('active');
      this.list.setAttribute('role', 'application');
      this.list.setAttribute('tabindex', -1);
      
      this.focus(this.list);
      this.announceAction('ACTIVATE');
    }
  }
  
  /*
   * Disable the active item so it's no longer `draggable`.
   */
  makeInactive() {
    if (this.list.contains(document.activeElement)) {
      this.focus(this.activeItem.querySelector('button'));
    }
    
    this.activeItem.classList.remove('active');
    this.list.removeAttribute('role');
    this.list.removeAttribute('tabindex');

    this.activeItem = null;
    this.listItems = null;
  }
  
  /*
   * Reposition the item being `dragged` on Up key press.
   */
  moveItemUp() {
    if (this.activeItem) {
      const sibling = this.activeItem.previousElementSibling;
      
      // Don't move up if already positioned as first item in the list.
      if (sibling) {
        this.list.insertBefore(this.activeItem, sibling);
        this.announceAction('MOVE');
      }
    }
  }
  
  /*
   * Reposition the item being `dragged` on Down key press.
   */
  moveItemDown() {
    if (this.activeItem) {
      const sibling = this.activeItem.nextElementSibling;
      
      // Don't move down if already positioned as last item in the list.
      if (sibling) {
        if (sibling.nextElementSibling) {
          this.list.insertBefore(this.activeItem, sibling.nextElementSibling);
        } else {
          this.list.appendChild(this.activeItem);
        }
        
        this.announceAction('MOVE');
      }
    }
  }
  
  /*
   * Resets the DOM when the `drag` operation is cancelled.
   */
  resetListOrder() {
    if (this.listItems) {
      Array.prototype.slice.call(this.listItems).forEach(item => {
        this.list.appendChild(item);
      });
    }
  }
  
  /*
   * Handles user cancelling `drag` operation.
   */
  cancelReorder() {
    if (this.activeItem) {
      this.announceAction('CANCEL');
      this.resetListOrder();
      this.makeInactive();
    }
  }
  
  /*
   * Confirm repositioning action, this is basically our keyboard based `drop` operation.
   */
  confirmPosition() {
    if (this.activeItem) {
      const newIndex = this.getItemIndex(this.activeItem).index;

      // Only confirm if item was actually moved.
      if (newIndex !== this.activeItem.index) {
        this.announceAction('CONFIRM');
        this.makeInactive();
      } else {
        this.announceAction('ACTIVATE');
      }
    }
  }
  
  /*
   * Click event handler.
   */
  handleClick(evt) {
    const target = evt.target;
    
    // Click on `drag` button handle activates item.
    if (target.tagName === 'BUTTON') {
      this.makeActive(target);
    }
  }
  
  /*
   * Keydown event handler.
   */
  handleKeydown(evt) {
    const KEY_CODES = {
      13: 'Enter',
      27: 'Escape',
      32: ' ',
      38: 'ArrowUp',
      40: 'ArrowDown'
    };
    const target = evt.target;
    // Use the new key event API, but defer to keyCode for non-supporting browsers.
    const key = evt.key || KEY_CODES[evt.keyCode];
    
    // Of course IE/Edge uses non-standards key names.
    switch (key) {
      case 'Spacebar': // IE/Edge
      case ' ':
      case 'Enter':
        if (target === evt.currentTarget) {
          this.confirmPosition();
        }
        break;
      case 'Esc': // IE/Edge
      case 'Escape':
        this.cancelReorder();
        break;
      case 'Up': // IE/Edge
      case 'ArrowUp':
        if (target === evt.currentTarget) {
          // Prevents undesirable arrow key behavior in Safari.
          evt.preventDefault();
          this.moveItemUp();
        }
        break;
      case 'Down': // IE/Edge
      case 'ArrowDown':
        if (target === evt.currentTarget) {
          // Prevents undesirable arrow key behavior in Safari.
          evt.preventDefault();
          this.moveItemDown();
        }
        break;
    }
  }
  
  /*
   * If the user tabs out of the list, cancel repositioning operation.
   */
  handleFocusout() {
    setTimeout(() => {
      if (this.activeItem && !this.list.contains(document.activeElement)) {
        this.cancelReorder();
      }
    }, 50);
  }
};



// WEBPACK FOOTER //
// in/scripts/dragdrop-keyboard.js